import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError, tokenStore } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Plus, X, Settings as SettingsIcon, Save, FileText, Users as UsersIcon, Upload, Image as ImageIcon, Trash2, Database, Download, RefreshCw, AlertTriangle, CheckCircle2, Cloud, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { QuoteSettingsPanel } from "@/pages/QuoteSettings";

const TABS_ALL = [
  { key: "general", label: "General", icon: SettingsIcon, roles: ["super_admin", "dealer"] },
  { key: "quote",   label: "Quote",   icon: FileText,      roles: ["super_admin"] },
  { key: "dealer",  label: "Dealer",  icon: UsersIcon,     roles: ["super_admin", "dealer"] },
  { key: "stations", label: "Production", icon: RefreshCw, roles: ["super_admin"] },
  { key: "costing",  label: "Costing",     icon: FileText,   roles: ["super_admin", "accounts", "admin"] },
  { key: "backup",  label: "Backup",  icon: Database,      roles: ["super_admin"] },
];

export default function Settings() {
  const { user } = useAuth();
  const TABS = TABS_ALL.filter((t) => t.roles.includes(user?.role || ""));
  const location = useLocation();
  const navigate = useNavigate();
  const queryTab = new URLSearchParams(location.search).get("tab");
  const defaultTab = TABS.find((t) => t.key === queryTab)?.key || (user?.role === "dealer" ? "dealer" : user?.role === "accounts" ? "costing" : "general");
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => {
    if (queryTab && TABS.some((t) => t.key === queryTab) && queryTab !== tab) setTab(queryTab);
  }, [queryTab, tab, TABS]);

  const setTabUrl = (k) => {
    setTab(k);
    navigate(`/settings?tab=${k}`, { replace: true });
  };

  return (
    <DesktopLayout>
      <div className="mb-6">
        <div className="hk-tiny-label">Administration</div>
        <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>Settings</h1>
        <p className="text-slate-500 mt-2 text-sm">All configuration in one place. Choose a section below.</p>
      </div>

      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTabUrl(t.key)}
            data-testid={`settings-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "general" && <GeneralPanel />}
      {tab === "quote"   && <QuoteSettingsPanel />}
      {tab === "dealer"  && <DealerPanel />}
      {tab === "stations" && <StationsPanel />}
      {tab === "costing" && <CostingPanel />}
      {tab === "backup"  && <BackupPanel />}
    </DesktopLayout>
  );
}

// ---------- Backup / restore ----------
function BackupPanel() {
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [lastRestore, setLastRestore] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/backup/list"),
        api.get("/backup/status"),
      ]);
      setBackups(list.backups || []);
      setStatus(st);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const gdriveOk = !!status?.gdrive?.enabled && !status?.gdrive?.error;

  const runBackup = async () => {
    if (!gdriveOk) return toast.error("Google Drive is not linked — configure it in Quote Settings → Drive first.");
    setBusy(true);
    try {
      const { data } = await api.post("/backup/create");
      toast.success(`Backup created: ${data.name}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const restoreFromDrive = async (fileId, name) => {
    if (!window.confirm(
      `Restore from "${name}"?\n\n` +
      "This will MERGE the backup into the current database:\n" +
      "  • existing records are updated to match the backup\n" +
      "  • records created after the backup are kept as-is\n" +
      "  • deletions in the backup are NOT undone\n\n" +
      "This action is irreversible without another backup."
    )) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/backup/restore/drive", { file_id: fileId });
      setLastRestore(data);
      const total = Object.values(data.summary || {}).reduce(
        (s, x) => s + (x.inserted || 0) + (x.updated || 0), 0
      );
      toast.success(`Restore complete — ${total} record(s) merged`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(false); }
  };

  const restoreFromFile = async () => {
    if (!file) return toast.error("Choose a backup file first");
    if (!window.confirm(
      `Restore from local file "${file.name}"?\n\n` +
      "MERGE mode — existing records will be overwritten with the backup version.\n" +
      "Records created after the backup are preserved. Deletions are not undone."
    )) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/restore/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastRestore(data);
      setFile(null);
      const total = Object.values(data.summary || {}).reduce(
        (s, x) => s + (x.inserted || 0) + (x.updated || 0), 0
      );
      toast.success(`Restore complete — ${total} record(s) merged`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(false); }
  };

  const removeBackup = async (fileId, name) => {
    if (!window.confirm(`Delete backup "${name}" from Google Drive? This cannot be undone.`)) return;
    try {
      await api.delete(`/backup/${fileId}`);
      toast.success("Backup deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="backup-panel">
      {/* Drive status banner */}
      <div className={`hk-card p-5 flex items-start gap-4 ${gdriveOk ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-amber-500"}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${gdriveOk ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
          {gdriveOk ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <div className="font-bold">{gdriveOk ? "Google Drive is linked" : "Google Drive is not configured"}</div>
          {gdriveOk ? (
            <div className="text-sm text-slate-600 mt-0.5">
              Backups are uploaded to <span className="font-mono font-semibold">{status?.gdrive?.folder_name}</span> → <span className="font-mono">Backups/</span>
            </div>
          ) : (
            <div className="text-sm text-slate-600 mt-0.5">
              Link a Google Drive service account under <b>Quote Settings → Drive</b> to enable backup uploads.
            </div>
          )}
          {status?.last_auto && (
            <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-2">
              <Cloud className="w-3 h-3" /> Last auto-backup: <span className="font-mono">{status.last_auto.file_name}</span> · {fmtDate(status.last_auto.at)}
            </div>
          )}
        </div>
        <button
          onClick={runBackup}
          disabled={busy || !gdriveOk}
          className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="backup-run-now"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
          Backup now
        </button>
      </div>

      {/* What is backed up */}
      <div className="hk-card p-5 text-sm text-slate-700">
        <div className="hk-tiny-label mb-2">What&apos;s inside a backup</div>
        <p className="mb-2">
          Every backup is a single <span className="font-mono">.zip</span> containing all your data as JSON — users, customers, projects, all quote versions, catalog (modules, materials, doors, handles, accessories, panel types, constants), issues, project settings, dealer profiles and file metadata. The actual uploaded PDFs / images stay in Google Drive and are re-linked automatically on restore.
        </p>
        <p>
          Automatic backups run once a month. Manual backups can be triggered anytime with the button above.
        </p>
      </div>

      {/* List of backups */}
      <div className="hk-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Backups in Google Drive</h3>
          <button
            onClick={load}
            disabled={loading}
            className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 text-sm font-semibold"
            data-testid="backup-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[11px] font-bold border-b border-slate-200">
              <tr>
                <th className="text-left px-5 py-2.5">File name</th>
                <th className="text-left px-5 py-2.5">Created</th>
                <th className="text-right px-5 py-2.5">Size</th>
                <th className="text-right px-5 py-2.5 w-72">Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.id} className="border-b border-slate-100" data-testid={`backup-row-${b.id}`}>
                  <td className="px-5 py-3 font-mono font-semibold">{b.name}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                  <td className="px-5 py-3 text-right text-slate-600">{fmtBytes(b.size)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      {b.view_link && (
                        <a href={b.view_link} target="_blank" rel="noopener noreferrer"
                           className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-xs font-semibold" title="Open in Google Drive">
                          <ExternalLink className="w-3.5 h-3.5" /> Open
                        </a>
                      )}
                      <button
                        onClick={() => restoreFromDrive(b.id, b.name)}
                        disabled={restoring}
                        className="text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 text-xs font-bold disabled:opacity-50"
                        data-testid={`backup-restore-${b.id}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Restore
                      </button>
                      <button
                        onClick={() => removeBackup(b.id, b.name)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Delete backup"
                        data-testid={`backup-del-${b.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {backups.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-slate-500">
                    {loading ? "Loading backups…" : "No backups yet. Click \"Backup now\" above to create one."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Restore from local file */}
      <div className="hk-card p-5">
        <h3 className="font-bold mb-2" style={{ fontFamily: "Chivo" }}>Restore from a local file</h3>
        <p className="text-sm text-slate-600 mb-4">
          If Google Drive is unreachable, upload a previously-downloaded <span className="font-mono">.zip</span> backup here.
          Uses the same merge behaviour: existing records get overwritten, new records are inserted.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 font-bold px-4 py-2 rounded-sm text-sm cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>{file ? file.name : "Choose backup .zip"}</span>
            <input
              type="file"
              accept=".zip,application/zip,application/json"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="backup-file-input"
            />
          </label>
          <button
            onClick={restoreFromFile}
            disabled={!file || restoring}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="backup-restore-upload"
          >
            {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Restore from upload
          </button>
        </div>
      </div>

      {/* Last restore result */}
      {lastRestore && (
        <div className="hk-card p-5" data-testid="backup-restore-result">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Last restore</h3>
          </div>
          <div className="text-xs text-slate-500 mb-3">from backup dated {fmtDate(lastRestore.restored_from)}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {Object.entries(lastRestore.summary || {}).map(([coll, s]) => (
              <div key={coll} className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                <div className="font-mono text-slate-500">{coll}</div>
                <div className="font-bold text-slate-800">
                  {s.inserted || 0} new · {s.updated || 0} updated
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- General ----------
function GeneralPanel() {
  return (
    <div className="hk-card p-6 max-w-2xl" data-testid="general-panel">
      <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "Chivo" }}>App-wide preferences</h3>
      <p className="text-sm text-slate-600 mb-4">More app-level toggles will land here. Look in <b>Quote → Google Drive</b> for the Drive integration status and in <b>Backup</b> to schedule / restore snapshots.</p>
      <ul className="text-sm text-slate-700 space-y-1.5 list-disc list-inside">
        <li>Theme: <span className="font-mono text-slate-500">Locked to brand defaults (orange / Chivo)</span></li>
        <li>Currency: <span className="font-mono text-slate-500">INR ₹</span></li>
        <li>Timezone: <span className="font-mono text-slate-500">Asia/Kolkata</span></li>
        <li>Date format: <span className="font-mono text-slate-500">DD MMM YYYY</span></li>
      </ul>
    </div>
  );
}

// ---------- Project (legacy settings) ----------
function OptionListEditor({ label, values, saved, onChange, onSave, busy, testidPrefix }) {
  const [draftItem, setDraftItem] = useState("");
  const dirty = JSON.stringify(values) !== JSON.stringify(saved);

  const add = () => {
    const v = draftItem.trim();
    if (!v) return;
    if (values.includes(v)) { toast.error("Option already exists"); return; }
    onChange([...values, v]);
    setDraftItem("");
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const ni = i + dir;
    if (ni < 0 || ni >= values.length) return;
    const next = [...values];
    [next[i], next[ni]] = [next[ni], next[i]];
    onChange(next);
  };

  return (
    <div className="hk-card p-6" data-testid={`setting-${testidPrefix}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
          <SettingsIcon className="w-4 h-4 text-slate-500" />
          {label}
        </h3>
        {dirty && <span className="hk-badge hk-badge-approval">Unsaved</span>}
      </div>
      <ul className="space-y-2 mb-4">
        {values.map((v, i) => (
          <li key={`${v}-${i}`} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm" data-testid={`setting-item-${testidPrefix}-${i}`}>
            <span className="font-semibold">{v}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-700 px-1" aria-label="Move up">↑</button>
              <button type="button" onClick={() => move(i, 1)} className="text-slate-400 hover:text-slate-700 px-1" aria-label="Move down">↓</button>
              <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-red-600 px-1" aria-label="Remove" data-testid={`setting-remove-${testidPrefix}-${i}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
        {values.length === 0 && <li className="text-sm text-slate-400">No options yet — add one below.</li>}
      </ul>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draftItem}
          onChange={(e) => setDraftItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 bg-white border border-slate-300 rounded-sm p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          placeholder="Add option…"
          data-testid={`setting-input-${testidPrefix}`}
        />
        <button type="button" onClick={add} className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-900 text-white font-bold px-3 py-2 rounded-sm text-sm" data-testid={`setting-add-${testidPrefix}`}>
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={busy || !dirty}
        className="mt-4 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`setting-save-${testidPrefix}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save changes
      </button>
    </div>
  );
}

// ---------- Dealer panel ----------
const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";
const LABEL = "hk-tiny-label block mb-1";

function DealerPanel() {
  const { user } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [meProfile, setMeProfile] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      if (user?.role === "dealer") {
        const { data } = await api.get("/dealer-profiles/me");
        setMeProfile(data);
      } else {
        const { data } = await api.get("/dealer-profiles");
        setDealers(data);
      }
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [user?.role]);

  if (err) return <div className="text-red-600">{err}</div>;

  if (user?.role === "dealer") {
    if (!meProfile) return <div className="text-slate-500">Loading your profile…</div>;
    return (
      <div data-testid="dealer-self-panel">
        <p className="text-sm text-slate-600 mb-4 max-w-2xl">
          The details below print on the header and footer of every quote PDF <b>you</b> create. Fill these in to brand your own client-facing quotes.
        </p>
        <DealerProfileForm targetUserId={user.id} initial={meProfile} onSaved={load} />
      </div>
    );
  }

  return (
    <div data-testid="dealer-admin-panel">
      <p className="text-sm text-slate-600 mb-4 max-w-2xl">
        Every dealer can have their own logo, address, GSTIN and T&amp;Cs printed on their quote PDFs. Edit here on their behalf, or let them edit it from their own login.
      </p>
      <ul className="space-y-3">
        {dealers.length === 0 && <li className="text-slate-400 text-sm">No dealer users yet — create one in Users.</li>}
        {dealers.map((d) => (
          <DealerCard key={d.user_id} dealer={d} onSaved={load} />
        ))}
      </ul>
    </div>
  );
}

function DealerCard({ dealer, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="hk-card overflow-hidden" data-testid={`dealer-card-${dealer.email}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50"
      >
        <div>
          <div className="font-bold">{dealer.name} <span className="text-slate-400 font-mono text-xs ml-2">{dealer.email}</span></div>
          <div className="text-xs text-slate-500 mt-0.5">
            {dealer.profile?.company_name || <span className="italic">No company name set</span>}
            {dealer.profile?.gstin && <> · GSTIN {dealer.profile.gstin}</>}
          </div>
        </div>
        <span className="hk-tiny-label">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50">
          <DealerProfileForm targetUserId={dealer.user_id} initial={dealer.profile} onSaved={onSaved} />
        </div>
      )}
    </li>
  );
}

function DealerProfileForm({ targetUserId, initial, onSaved }) {
  const [state, setState] = useState(initial || {});
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setState(initial || {}), [initial]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/dealer-profiles/${targetUserId}`, {
        company_name: state.company_name || "",
        address: state.address || "",
        phone: state.phone || "",
        email: state.email || "",
        gstin: state.gstin || "",
        terms_and_conditions: state.terms_and_conditions || "",
      });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/dealer-profiles/${targetUserId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setLogoFile(null);
      }
      toast.success("Saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className={LABEL}>Company name (as printed on PDF)</label>
        <input className={FIELD} value={state.company_name || ""} onChange={(e) => setState({ ...state, company_name: e.target.value })} data-testid="dealer-company-name" />
      </div>
      <div>
        <label className={LABEL}>GSTIN</label>
        <input className={FIELD} value={state.gstin || ""} onChange={(e) => setState({ ...state, gstin: e.target.value })} data-testid="dealer-gstin" />
      </div>
      <div>
        <label className={LABEL}>Phone</label>
        <input className={FIELD} value={state.phone || ""} onChange={(e) => setState({ ...state, phone: e.target.value })} data-testid="dealer-phone" />
      </div>
      <div>
        <label className={LABEL}>Email</label>
        <input className={FIELD} value={state.email || ""} onChange={(e) => setState({ ...state, email: e.target.value })} data-testid="dealer-email" />
      </div>
      <div className="md:col-span-2">
        <label className={LABEL}>Address</label>
        <textarea className={FIELD} rows={2} value={state.address || ""} onChange={(e) => setState({ ...state, address: e.target.value })} data-testid="dealer-address" />
      </div>
      <div className="md:col-span-2">
        <label className={LABEL}>Terms &amp; Conditions (plain text, printed verbatim)</label>
        <textarea className={FIELD} rows={5} value={state.terms_and_conditions || ""} onChange={(e) => setState({ ...state, terms_and_conditions: e.target.value })} data-testid="dealer-tnc" />
      </div>
      <div className="md:col-span-2">
        <label className={LABEL}>Logo (PNG / JPG)</label>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-bold px-4 py-2 rounded-sm text-sm cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>{logoFile ? logoFile.name : "Choose file"}</span>
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="dealer-logo" />
          </label>
          {state.logo_path && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Current logo on file</span>}
        </div>
      </div>
      <div className="md:col-span-2">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2 rounded-sm disabled:opacity-50" data-testid="dealer-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save dealer profile
        </button>
      </div>
    </div>
  );
}

// ---------- Production + Installation Stations (iter 89) ----------
// Split into two visually-distinct sub-panels. The owner_role dropdown
// is gone — a station's owner is derived from which section it lives in,
// so we send { key, name, order, owner_role } to the backend the same
// way but the UX is now completely un-confusable.
function StationsPanel() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/project/stations"); setStations(data.stations || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const production = stations.filter((s) => (s.owner_role || "production") === "production");
  const installation = stations.filter((s) => s.owner_role === "installation");

  const save = async () => {
    setBusy(true);
    // Rebuild the flat list preserving section order — production first,
    // then installation — and stamp fresh `order` values.
    const flat = [...production, ...installation].map((s, idx) => ({
      key: s.key || "",
      name: s.name || "",
      order: idx + 1,
      owner_role: production.includes(s) ? "production" : "installation",
    }));
    try {
      const { data } = await api.put("/project/stations", { stations: flat });
      setStations(data.stations);
      toast.success("Stations saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // All mutations run on the flat `stations` array to keep the source of
  // truth in one place; the per-section rendering is a view.
  const idxInFlat = (row) => stations.indexOf(row);
  const move = (row, dir) => {
    const roleList = row.owner_role === "installation" ? installation : production;
    const localIdx = roleList.indexOf(row);
    const j = localIdx + dir;
    if (j < 0 || j >= roleList.length) return;
    const next = [...stations];
    const a = idxInFlat(row);
    const b = idxInFlat(roleList[j]);
    [next[a], next[b]] = [next[b], next[a]];
    setStations(next);
  };
  const rename = (row, name) => {
    const next = [...stations];
    next[idxInFlat(row)] = { ...row, name };
    setStations(next);
  };
  const remove = (row) => setStations(stations.filter((s) => s !== row));
  const add = (owner_role) => setStations([
    ...stations,
    { key: "", name: "", order: stations.length + 1, owner_role },
  ]);

  if (loading) return <div className="hk-card p-8 text-center text-slate-500">Loading stations…</div>;

  return (
    <div className="space-y-4" data-testid="stations-panel">
      <SectionCard
        title="Production Stations"
        subtitle="Handled by the Production team. Copied to every new project at Prod Coord handoff. Existing projects sync automatically when you save."
        accent="#F97316"
        rows={production}
        onMove={move} onRename={rename} onRemove={remove}
        addLabel="Add production station"
        onAdd={() => add("production")}
        testId="stations-production" />
      <SectionCard
        title="Installation Stations"
        subtitle="Handled by the Installation team. Same rules — copied onto new projects; safe to reorder or rename anytime."
        accent="#059669"
        rows={installation}
        onMove={move} onRename={rename} onRemove={remove}
        addLabel="Add installation station"
        onAdd={() => add("installation")}
        testId="stations-installation" />
      <div className="flex justify-end">
        <button onClick={save} disabled={busy}
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-sm text-sm disabled:opacity-50"
          data-testid="stations-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save all stations
        </button>
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, accent, rows, onMove, onRename, onRemove, addLabel, onAdd, testId }) {
  return (
    <div className="hk-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-bold" style={{ fontFamily: "Chivo", color: accent }}>{title}</h3>
          <p className="text-sm text-slate-600 mt-0.5">{subtitle}</p>
        </div>
        <span className="text-xs font-mono px-2 py-1 rounded-sm"
          style={{ backgroundColor: `${accent}15`, color: accent }}>
          {rows.length} station{rows.length === 1 ? "" : "s"}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="text-center text-slate-400 text-xs py-6 border border-dashed border-slate-200 rounded-sm">
          No stations here yet. Add one below.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((s, i) => (
            <li key={`${s.key || "new"}-${i}`} className="flex items-center gap-2 border border-slate-200 rounded-sm p-2 bg-white">
              <span className="text-xs text-slate-400 w-6 text-right">{i + 1}.</span>
              <input className="flex-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                value={s.name} placeholder="Station name"
                onChange={(e) => onRename(s, e.target.value)}
                data-testid={`${testId}-name-${i}`} />
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => onMove(s, -1)}
                  className="text-slate-500 hover:text-slate-900 px-1.5"
                  data-testid={`${testId}-up-${i}`}>↑</button>
                <button type="button" onClick={() => onMove(s, 1)}
                  className="text-slate-500 hover:text-slate-900 px-1.5"
                  data-testid={`${testId}-down-${i}`}>↓</button>
                <button type="button" onClick={() => onRemove(s)}
                  className="text-red-500 hover:text-red-700 px-1.5"
                  data-testid={`${testId}-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3">
        <button onClick={onAdd}
          className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-800 font-bold px-3 py-2 rounded-sm text-sm"
          data-testid={`${testId}-add`}>
          <Plus className="w-3.5 h-3.5" /> {addLabel}
        </button>
      </div>
    </div>
  );
}


/** Costing tab — Vendor master + labour rates + sheet material rates.
 * Gated to super_admin + accounts. Downstream phases will use these values
 * to compute per-project margins. */
function CostingPanel() {
  const [vendors, setVendors] = useState([]);
  const [stationRates, setStationRates] = useState({});
  const [sheetRates, setSheetRates] = useState({});
  const [stations, setStations] = useState([]);      // canonical list from Settings > Production Stations
  const [materials, setMaterials] = useState([]);    // canonical list from Settings > Quote > Materials
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", gst: "", address: "", phone: "", email: "", notes: "" });
  const load = async () => {
    setLoading(true);
    try {
      const [v, sr, mr, st, cat] = await Promise.all([
        api.get("/costing/vendors"),
        api.get("/costing/station-rates"),
        api.get("/costing/sheet-rates"),
        api.get("/project/stations"),
        api.get("/quote/catalog"),
      ]);
      setVendors(v.data || []);
      setStationRates(sr.data.rates || {});
      setSheetRates(mr.data.rates || {});
      setStations(st.data?.stations || []);
      setMaterials(cat.data?.materials || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createVendor = async () => {
    if (!form.name.trim()) { toast.error("Vendor name is required"); return; }
    setBusy(true);
    try {
      await api.post("/costing/vendors", form);
      setForm({ name: "", gst: "", address: "", phone: "", email: "", notes: "" });
      toast.success("Vendor added");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const removeVendor = async (v) => {
    if (!window.confirm(`Remove vendor "${v.name}"?`)) return;
    try {
      await api.delete(`/costing/vendors/${v.id}`);
      toast.success("Vendor removed");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveStationRates = async () => {
    setBusy(true);
    try {
      // Only ship rates for stations that currently exist in the config.
      const validKeys = new Set(stations.map((s) => s.key));
      const cleaned = {};
      for (const [k, v] of Object.entries(stationRates)) {
        if (validKeys.has(k)) cleaned[k] = Number(v) || 0;
      }
      await api.put("/costing/station-rates", { rates: cleaned });
      setStationRates(cleaned);
      toast.success("Station rates saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const saveSheetRates = async () => {
    setBusy(true);
    try {
      const validNames = new Set(materials.map((m) => m.name));
      const cleaned = {};
      for (const [k, v] of Object.entries(sheetRates)) {
        if (validNames.has(k)) cleaned[k] = Number(v) || 0;
      }
      await api.put("/costing/sheet-rates", { rates: cleaned });
      setSheetRates(cleaned);
      toast.success("Material rates saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="hk-card p-8 text-center text-slate-500">Loading costing data…</div>;
  return (
    <div className="space-y-6" data-testid="costing-panel">
      {/* Vendors */}
      <div className="hk-card p-5">
        <h3 className="text-lg font-bold mb-1" style={{ fontFamily: "Chivo" }}>Vendors</h3>
        <p className="text-sm text-slate-600 mb-4">Central vendor list used for purchase orders.</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          <input className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" placeholder="Name*" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" />
          <input className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" placeholder="GSTIN" value={form.gst}
            onChange={(e) => setForm({ ...form, gst: e.target.value })} data-testid="vendor-gst" />
          <input className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" placeholder="Phone" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-phone" />
          <input className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-email" />
          <input className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm sm:col-span-2" placeholder="Address" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="vendor-address" />
        </div>
        <button onClick={createVendor} disabled={busy}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-black uppercase px-4 py-2 rounded-sm"
          data-testid="vendor-add">Add vendor</button>
        <ul className="mt-4 divide-y divide-slate-100">
          {vendors.length === 0 && <li className="text-sm text-slate-400 py-3">No vendors yet.</li>}
          {vendors.map((v) => (
            <li key={v.id} className="py-2 flex items-start justify-between gap-2" data-testid={`vendor-row-${v.id}`}>
              <div className="min-w-0">
                <div className="font-bold text-sm text-slate-900">{v.name}</div>
                <div className="text-[11px] text-slate-500">
                  {[v.gst, v.phone, v.email, v.address].filter(Boolean).join(" · ")}
                </div>
              </div>
              <button onClick={() => removeVendor(v)} className="text-rose-500 hover:text-rose-700 text-xs" data-testid={`vendor-delete-${v.id}`}>Remove</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Station labour rates — auto-populated from Production Stations */}
      <div className="hk-card p-5">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div>
            <h3 className="text-lg font-bold" style={{ fontFamily: "Chivo" }}>Station labour rates (₹ / hr)</h3>
            <p className="text-sm text-slate-600">
              Auto-listed from <b>Settings → Production Stations</b>. Add / rename stations there;
              this panel will reflect it. Set ₹/hr for each and save.
            </p>
          </div>
          <button onClick={saveStationRates} disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-black uppercase px-4 py-1.5 rounded-sm"
            data-testid="station-rates-save">Save</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {stations.length === 0 && (
            <div className="text-sm text-slate-400 py-2 sm:col-span-2">
              No production stations configured. Add them in Settings → Production Stations.
            </div>
          )}
          {stations.map((s) => (
            <div key={s.key} className="flex items-center gap-2" data-testid={`station-rate-${s.key}`}>
              <span className="text-xs font-bold w-40 truncate" title={s.key}>{s.name}</span>
              <input type="number" step="1" min="0" value={stationRates[s.key] ?? 0}
                onChange={(e) => setStationRates({ ...stationRates, [s.key]: e.target.value })}
                className="flex-1 border border-slate-300 rounded-sm px-2 py-1 text-sm"
                data-testid={`station-rate-input-${s.key}`} />
              <span className="text-xs text-slate-500">₹/hr</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sheet material rates — auto-populated from Quote Materials */}
      <div className="hk-card p-5">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div>
            <h3 className="text-lg font-bold" style={{ fontFamily: "Chivo" }}>Sheet material rates (₹ / kg)</h3>
            <p className="text-sm text-slate-600">
              Auto-listed from <b>Settings → Quote → Materials</b>. Rate here is used to price
              sheet consumption (L×W×T×qty × 8 × ₹/kg).
            </p>
          </div>
          <button onClick={saveSheetRates} disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-black uppercase px-4 py-1.5 rounded-sm"
            data-testid="sheet-rates-save">Save</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {materials.length === 0 && (
            <div className="text-sm text-slate-400 py-2 sm:col-span-2">
              No materials configured. Add them in Settings → Quote → Materials.
            </div>
          )}
          {materials.map((m) => (
            <div key={m.id || m.name} className="flex items-center gap-2" data-testid={`sheet-rate-${m.name}`}>
              <span className="text-xs font-bold w-40 truncate" title={m.name}>{m.name}</span>
              <input type="number" step="0.1" min="0" value={sheetRates[m.name] ?? 0}
                onChange={(e) => setSheetRates({ ...sheetRates, [m.name]: e.target.value })}
                className="flex-1 border border-slate-300 rounded-sm px-2 py-1 text-sm"
                data-testid={`sheet-rate-input-${m.name}`} />
              <span className="text-xs text-slate-500">₹/kg</span>
              {m.rate_per_kg ? (
                <span className="text-[10px] text-slate-400" title="Rate defined on the material in Quote Settings">
                  quote ref: ₹{m.rate_per_kg}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

